import React, { useState, useEffect, useCallback } from "react";
import { Layout, Modal, Button, Tag, Divider, Typography, Input } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";

const { Content } = Layout;
const { TextArea } = Input;

/* ─── Status Config ─────────────────────────────────────────────────────── */
// Backend: aprovado = "S" (aprovado) | "N" (recusado) | "V" ou null (pendente)
const STATUS_CONFIG = {
  S: { bg: "#D1FAE5", color: "#065F46", dot: "#10B981", label: "Aprovada",  tagColor: "success" },
  N: { bg: "#FEE2E2", color: "#991B1B", dot: "#EF4444", label: "Recusada",  tagColor: "error"   },
  P: { bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B", label: "Pendente",  tagColor: "gold"    },
};

const getStatusCfg = (aprovado) => STATUS_CONFIG[aprovado] ?? STATUS_CONFIG["P"];
const isPendente = (aprovado) => aprovado === "P" || aprovado === null || aprovado === "" || aprovado === undefined;

/* ─── Skeleton ───────────────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div style={{ background: "#fff", border: "1px solid #F3F4F6", borderRadius: 14, padding: 18, marginBottom: 12 }}>
    {[80, 120, 60].map((w, i) => (
      <div key={i} style={{
        height: 12, width: `${w}%`, borderRadius: 6, marginBottom: 10,
        background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
        backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite",
      }} />
    ))}
  </div>
);

/* ─── DetailRow ──────────────────────────────────────────────────────────── */
const DetailRow = ({ label, value, icon }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    {icon && <span style={{ color: "#9CA3AF", fontSize: 14 }}>{icon}</span>}
    <span style={{ fontSize: 13, color: "#6B7280", minWidth: 130 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{value}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════════════════════ */
const AprovacaoFeriasSecretariado = () => {
  const userStored = JSON.parse(localStorage.getItem("user") || "{}");
  const diretor    = userStored.diretor || "N";
  const nomeUser   = userStored.nome || userStored.name || "Diretor";

  const [ferias,           setFerias]           = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [filtro,           setFiltro]           = useState("TODOS");

  // Modal detalhe (leitura)
  const [modalDetalheOpen, setModalDetalheOpen] = useState(false);
  const [feriaSelecionada, setFeriaSelecionada] = useState(null);

  // Modal confirmação de ação
  const [modalAcaoOpen,    setModalAcaoOpen]    = useState(false);
  const [acaoTipo,         setAcaoTipo]         = useState(null); // "S" = aprovar | "N" = recusar
  const [obs2,             setObs2]             = useState("");
  const [submitting,       setSubmitting]       = useState(false);

  useEffect(() => { document.title = "Aprovação de Férias | Dashboard"; }, []);

  /* ── Carregar lista ── */
  const loadFerias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Api.ListFeriasAprovacaoSecretariado();
      setFerias(Array.isArray(res) ? res : []);
    } catch { toast.error("Erro ao carregar solicitações."); }
    finally  { setLoading(false); }
  }, [diretor]);

  useEffect(() => { loadFerias(); }, [loadFerias]);

  /* ── Filtro ── */
  const feriasFiltradas = ferias.filter(f => {
    if (filtro === "TODOS")    return true;
    if (filtro === "PENDENTE") return isPendente(f.aprovado);
    if (filtro === "APROVADO") return f.aprovado === "S";
    if (filtro === "RECUSADO") return f.aprovado === "N";
    return true;
  });

  /* ── Abrir modal de ação ── */
  const abrirAcao = (f, tipo, e) => {
    e?.stopPropagation();
    setFeriaSelecionada(f);
    setAcaoTipo(tipo);
    setObs2("");
    setModalDetalheOpen(false);
    setModalAcaoOpen(true);
  };

  /* ── Submeter decisão ── */
  const submitAcao = async () => {
    if (!feriaSelecionada) return;
    setSubmitting(true);
    try {
      await Api.AprovarFeriaSecretariado({
        id:       feriaSelecionada.id,
     aprovado: acaoTipo === "S" ? "V" : "N",      // "S" (aprovar) ou "N" (recusar)
        aprovador: nomeUser,       // userStored.nome
        obs3:     obs2 || undefined,
      });
      toast.success(acaoTipo === "V" ? "✅ Férias aprovadas com sucesso!" : "❌ Férias recusadas.");
      setModalAcaoOpen(false);
      loadFerias();
    } catch { toast.error("Erro ao processar a decisão."); }
    finally  { setSubmitting(false); }
  };

  /* ── Stats ── */
  const totalPendentes = ferias.filter(f => isPendente(f.aprovado)).length;
  const stats = [
    { key: "TODOS",    label: "Total",     dot: "#6B7280", value: ferias.length },
    { key: "PENDENTE", label: "Pendentes", dot: "#F59E0B", value: totalPendentes },
    { key: "APROVADO", label: "Aprovadas", dot: "#10B981", value: ferias.filter(f => f.aprovado === "S").length },
    { key: "RECUSADO", label: "Recusadas", dot: "#EF4444", value: ferias.filter(f => f.aprovado === "N").length },
  ];

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <Layout style={{ background: "transparent", minHeight: "100vh" }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .aprov-card { transition: all 0.18s; }
        .aprov-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.09) !important; }
        .stat-chip  { transition: all 0.18s; cursor: pointer; }
        .stat-chip:hover { transform: translateY(-2px); }
        .btn-aprovar { transition: background 0.18s, transform 0.18s !important; }
        .btn-aprovar:hover { background: #A7F3D0 !important; transform: translateY(-1px); }
        .btn-recusar { transition: background 0.18s, transform 0.18s !important; }
        .btn-recusar:hover { background: #FECACA !important; transform: translateY(-1px); }
      `}</style>

      <Content style={{ padding: "28px 36px", animation: "fadeUp 0.4s ease" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={{
          background: "linear-gradient(135deg, #0F4C8A 0%, #185FA5 50%, #2563EB 100%)",
          borderRadius: 18, padding: "26px 32px", marginBottom: 28,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 16px 48px rgba(24,95,165,0.28)",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{ position:"absolute", right:-30, top:-30, width:180, height:180, borderRadius:"50%", background:"rgba(255,255,255,0.06)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", right:60, bottom:-50, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,0.04)", pointerEvents:"none" }} />

          <div style={{ position: "relative" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </div>
              <h1 style={{ color:"#fff", fontSize:20, fontWeight:700, margin:0, letterSpacing:"-0.3px" }}>
                Aprovação de Férias
              </h1>
            </div>
            <p style={{ color:"rgba(255,255,255,0.70)", fontSize:13, margin:0 }}>
              Olá, <strong style={{ color:"rgba(255,255,255,0.92)" }}>{nomeUser}</strong> — Gerencie as solicitações da sua equipa
            </p>
          </div>

          {/* Badge pendentes */}
          {totalPendentes > 0 && (
            <div style={{ background:"#FEF3C7", borderRadius:12, padding:"12px 22px", display:"flex", alignItems:"center", gap:10 }}>
              <ExclamationCircleFilled style={{ color:"#D97706", fontSize:22 }} />
              <div>
                <div style={{ fontSize:24, fontWeight:800, color:"#92400E", lineHeight:1 }}>{totalPendentes}</div>
                <div style={{ fontSize:11, color:"#B45309", fontWeight:600 }}>A aguardar decisão</div>
              </div>
            </div>
          )}
        </div>

        {/* ── STATS / FILTROS ────────────────────────────────────────────── */}
        <div style={{ display:"flex", gap:10, marginBottom:24, flexWrap:"wrap" }}>
          {stats.map(s => (
            <div key={s.key} className="stat-chip" onClick={() => setFiltro(s.key)} style={{
              background:"#fff",
              border: `2px solid ${filtro === s.key ? s.dot : "#F3F4F6"}`,
              borderRadius:14, padding:"14px 20px", flex:"1 1 80px", minWidth:90,
              boxShadow: filtro === s.key ? `0 4px 16px ${s.dot}33` : "0 2px 8px rgba(0,0,0,0.04)",
              borderTop: `3px solid ${s.dot}`,
            }}>
              <div style={{ fontSize:24, fontWeight:700, color:s.dot, lineHeight:1 }}>
                {loading ? "—" : s.value}
              </div>
              <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4, fontWeight:500, letterSpacing:"0.2px" }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── LISTA ──────────────────────────────────────────────────────── */}
        <div style={{ background:"#fff", borderRadius:16, padding:20, border:"1px solid #E5E7EB", boxShadow:"0 4px 16px rgba(0,0,0,0.05)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>
              Solicitações {filtro !== "TODOS" ? `— ${stats.find(s => s.key === filtro)?.label}` : ""}
            </div>
            <div style={{ fontSize:12, color:"#9CA3AF" }}>
              {feriasFiltradas.length} registo{feriasFiltradas.length !== 1 ? "s" : ""}
            </div>
          </div>

          {loading ? (
            <>{[1,2,3].map(i => <SkeletonCard key={i}/>)}</>
          ) : feriasFiltradas.length === 0 ? (
            <div style={{ padding:"48px 24px", textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
              <div style={{ fontSize:15, fontWeight:600, color:"#374151", marginBottom:6 }}>Nenhuma solicitação encontrada</div>
              <div style={{ fontSize:13, color:"#9CA3AF" }}>Todas as solicitações foram processadas</div>
            </div>
          ) : feriasFiltradas.map(f => {
            const cfg  = getStatusCfg(f.aprovado);
            const dI   = dayjs(f.inicio), dF = dayjs(f.fim);
            const dias  = dF.diff(dI, "day") + 1;
            const pend  = isPendente(f.aprovado);

            return (
              <div key={f.id} className="aprov-card" style={{
                background:"#fff", border:"1px solid #E5E7EB", borderRadius:14,
                padding:"18px 20px", marginBottom:10,
                boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
                borderLeft:`4px solid ${cfg.dot}`,
              }}>

                {/* Linha superior: nome + badge */}
                <div
                  style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10, cursor:"pointer" }}
                  onClick={() => { setFeriaSelecionada(f); setModalDetalheOpen(true); }}
                >
                  <div>
                    <div style={{ fontSize:15, fontWeight:700, color:"#111827", marginBottom:2 }}>{f.colaborador}</div>
                    <div style={{ fontSize:12, color:"#9CA3AF" }}>Solicitação #{f.id}</div>
                  </div>
                  <Tag color={cfg.tagColor} style={{ fontWeight:700, fontSize:11, letterSpacing:"0.3px", textTransform:"uppercase" }}>
                    {cfg.label}
                  </Tag>
                </div>

                {/* Linha inferior: período + botões de ação */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>

                  {/* Período */}
                  <div
                    style={{ display:"flex", gap:16, fontSize:12, color:"#6B7280", flexWrap:"wrap", cursor:"pointer" }}
                    onClick={() => { setFeriaSelecionada(f); setModalDetalheOpen(true); }}
                  >
                    <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <span>📅</span>
                      {dI.format("DD/MM/YYYY")} → {dF.format("DD/MM/YYYY")}
                      <strong style={{ color:"#374151" }}>({dias} dia{dias !== 1 ? "s" : ""})</strong>
                    </span>
                    {f.obs && (
                      <span style={{ fontStyle:"italic", color:"#9CA3AF" }}>
                        "{f.obs.length > 45 ? f.obs.substring(0,45)+"..." : f.obs}"
                      </span>
                    )}
                  </div>

                  {/* ── BOTÕES APROVAR / RECUSAR (só pendentes) ── */}
                  {pend && (
                    <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                      <button
                        className="btn-aprovar"
                        onClick={(e) => abrirAcao(f, "S", e)}
                        style={{
                          display:"flex", alignItems:"center", gap:6,
                          padding:"8px 18px", borderRadius:10, border:"none",
                          background:"#D1FAE5", color:"#065F46",
                          fontSize:13, fontWeight:700, cursor:"pointer",
                        }}
                      >
                        <CheckOutlined /> Aprovar
                      </button>
                      <button
                        className="btn-recusar"
                        onClick={(e) => abrirAcao(f, "N", e)}
                        style={{
                          display:"flex", alignItems:"center", gap:6,
                          padding:"8px 18px", borderRadius:10, border:"none",
                          background:"#FEE2E2", color:"#991B1B",
                          fontSize:13, fontWeight:700, cursor:"pointer",
                        }}
                      >
                        <CloseOutlined /> Recusar
                      </button>
                    </div>
                  )}

                  {/* Info de quem processou (se já decidido) */}
                  {!pend && f.aprovador && f.aprovador !== "N" && (
                    <span style={{ fontSize:11, color:"#9CA3AF", fontStyle:"italic" }}>
                      {f.aprovado === "S" ? "✓" : "✕"} {f.aprovador}
                      {f.data_aprovacao ? ` · ${dayjs(f.data_aprovacao).format("DD/MM/YYYY")}` : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ══ MODAL DETALHE ════════════════════════════════════════════════ */}
        <Modal
          open={modalDetalheOpen}
          onCancel={() => setModalDetalheOpen(false)}
          width={520}
          title={null}
          closable
          maskClosable
          footer={
            feriaSelecionada && isPendente(feriaSelecionada.aprovado) ? (
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button
                  className="btn-recusar"
                  onClick={(e) => abrirAcao(feriaSelecionada, "N", e)}
                  style={{
                    display:"flex", alignItems:"center", gap:6, padding:"9px 22px",
                    borderRadius:10, border:"none", background:"#FEE2E2", color:"#991B1B",
                    fontSize:13, fontWeight:700, cursor:"pointer",
                  }}
                >
                  <CloseOutlined /> Recusar
                </button>
                <button
                  className="btn-aprovar"
                  onClick={(e) => abrirAcao(feriaSelecionada, "S", e)}
                  style={{
                    display:"flex", alignItems:"center", gap:6, padding:"9px 22px",
                    borderRadius:10, border:"none",
                    background:"linear-gradient(135deg,#059669,#10B981)", color:"#fff",
                    fontSize:13, fontWeight:700, cursor:"pointer",
                    boxShadow:"0 4px 12px rgba(5,150,105,0.3)",
                  }}
                >
                  <CheckOutlined /> Aprovar
                </button>
              </div>
            ) : null
          }
        >
          {feriaSelecionada && (() => {
            const cfg  = getStatusCfg(feriaSelecionada.aprovado);
            const dI   = dayjs(feriaSelecionada.inicio);
            const dF   = dayjs(feriaSelecionada.fim);
            const dias = dF.diff(dI, "day") + 1;
            return (
              <div style={{ padding:"8px 0 0" }}>
                <div style={{ marginBottom:20 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
                    <div style={{ width:42, height:42, borderRadius:12, background:cfg.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {feriaSelecionada.aprovado === "S"
                        ? <CheckCircleFilled  style={{ fontSize:22, color:"#065F46" }}/>
                        : feriaSelecionada.aprovado === "N"
                          ? <CloseCircleFilled style={{ fontSize:22, color:"#991B1B" }}/>
                          : <ClockCircleOutlined style={{ fontSize:22, color:"#92400E" }}/>
                      }
                    </div>
                    <div>
                      <Typography.Title level={4} style={{ margin:0, fontWeight:700, color:"#111827" }}>
                        Solicitação de Férias
                      </Typography.Title>
                      <Typography.Text style={{ fontSize:13, color:"#6B7280" }}>#{feriaSelecionada.id}</Typography.Text>
                    </div>
                  </div>
                  <Divider style={{ margin:"12px 0 0" }}/>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  <div style={{ background:"#F9FAFB", borderRadius:12, padding:"16px 18px", border:"1px solid #F3F4F6" }}>
                    <Typography.Text strong style={{ fontSize:13, color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:12 }}>
                      Colaborador
                    </Typography.Text>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      <DetailRow label="Nome"   icon={<UserOutlined/>}  value={feriaSelecionada.colaborador}/>
                      <DetailRow label="ID"     icon={null}             value={feriaSelecionada.employee_id}/>
                      <DetailRow label="Estado" icon={null}             value={<Tag color={cfg.tagColor} style={{fontWeight:500}}>{cfg.label.toUpperCase()}</Tag>}/>
                    </div>
                  </div>

                  <div style={{ background:"#F9FAFB", borderRadius:12, padding:"16px 18px", border:"1px solid #F3F4F6" }}>
                    <Typography.Text strong style={{ fontSize:13, color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:12 }}>
                      Período Solicitado
                    </Typography.Text>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      <DetailRow label="Início"        icon={<CalendarOutlined/>}    value={dI.format("DD/MM/YYYY")}/>
                      <DetailRow label="Fim"           icon={<CalendarOutlined/>}    value={dF.format("DD/MM/YYYY")}/>
                      <DetailRow label="Total de dias" icon={<ClockCircleOutlined/>} value={`${dias} dia${dias !== 1 ? "s" : ""}`}/>
                    </div>
                  </div>

                  {!isPendente(feriaSelecionada.aprovado) && feriaSelecionada.aprovador && feriaSelecionada.aprovador !== "N" && (
                    <div style={{ background:"#F9FAFB", borderRadius:12, padding:"16px 18px", border:"1px solid #F3F4F6" }}>
                      <Typography.Text strong style={{ fontSize:13, color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:12 }}>
                        Decisão
                      </Typography.Text>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        <DetailRow label="Processado por" icon={<UserOutlined/>}    value={feriaSelecionada.aprovador}/>
                        {feriaSelecionada.data_aprovacao && (
                          <DetailRow label="Data"         icon={<CalendarOutlined/>} value={dayjs(feriaSelecionada.data_aprovacao).format("DD/MM/YYYY HH:mm")}/>
                        )}
                      </div>
                    </div>
                  )}

                  {(feriaSelecionada.obs || feriaSelecionada.obs2) && (
                    <div style={{ background:"#F9FAFB", borderRadius:12, padding:"16px 18px", border:"1px solid #F3F4F6" }}>
                      <Typography.Text strong style={{ fontSize:13, color:"#6B7280", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:12 }}>
                        Observações
                      </Typography.Text>
                      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                        {feriaSelecionada.obs && (
                          <div>
                            <Typography.Text style={{ fontSize:12, color:"#9CA3AF" }}>Colaborador:</Typography.Text>
                            <div style={{ fontSize:13, color:"#374151", marginTop:2, whiteSpace:"pre-wrap" }}>{feriaSelecionada.obs}</div>
                          </div>
                        )}
                        {feriaSelecionada.obs2 && (
                          <div style={{ marginTop: feriaSelecionada.obs ? 6 : 0 }}>
                            <Typography.Text style={{ fontSize:12, color:"#9CA3AF" }}>Diretor:</Typography.Text>
                            <div style={{ fontSize:13, color:"#B91C1C", marginTop:2, whiteSpace:"pre-wrap" }}>{feriaSelecionada.obs2}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </Modal>

        {/* ══ MODAL CONFIRMAÇÃO DE AÇÃO ════════════════════════════════════ */}
        <Modal
          open={modalAcaoOpen}
          onCancel={() => setModalAcaoOpen(false)}
          footer={null}
          width={440}
          title={
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{
                width:38, height:38, borderRadius:10, flexShrink:0,
                background: acaoTipo === "S" ? "#D1FAE5" : "#FEE2E2",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                {acaoTipo === "S"
                  ? <CheckCircleFilled  style={{ fontSize:20, color:"#065F46" }}/>
                  : <CloseCircleFilled  style={{ fontSize:20, color:"#991B1B" }}/>
                }
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>
                  {acaoTipo === "S" ? "Aprovar Férias" : "Recusar Férias"}
                </div>
                <div style={{ fontSize:12, color:"#9CA3AF", fontWeight:400 }}>{feriaSelecionada?.colaborador}</div>
              </div>
            </div>
          }
        >
          {feriaSelecionada && (
            <div style={{ paddingTop:8 }}>
              {/* Resumo */}
              <div style={{
                background: acaoTipo === "S" ? "#F0FDF4" : "#FFF5F5",
                borderRadius:10, padding:"14px 16px", marginBottom:20,
                border:`1px solid ${acaoTipo === "S" ? "#BBF7D0" : "#FECACA"}`,
              }}>
                <div style={{ fontSize:13, fontWeight:600, color: acaoTipo === "S" ? "#065F46" : "#991B1B", marginBottom:6 }}>
                  {acaoTipo === "S" ? "✓ Confirmar aprovação" : "✕ Confirmar recusa"}
                </div>
                <div style={{ fontSize:12, color:"#374151", fontWeight:500, marginBottom:2 }}>👤 {feriaSelecionada.colaborador}</div>
                <div style={{ fontSize:12, color:"#6B7280" }}>
                  📅 {dayjs(feriaSelecionada.inicio).format("DD/MM/YYYY")} → {dayjs(feriaSelecionada.fim).format("DD/MM/YYYY")}
                  {" · "}{dayjs(feriaSelecionada.fim).diff(dayjs(feriaSelecionada.inicio),"day") + 1} dias
                </div>
              </div>

              {/* obs2 */}
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:12, color:"#6B7280", fontWeight:600, display:"block", marginBottom:6 }}>
                  Observação para o colaborador <span style={{ color:"#9CA3AF", fontWeight:400 }}>(opcional)</span>
                </label>
                <TextArea
                  rows={3}
                  value={obs2}
                  onChange={e => setObs2(e.target.value)}
                  placeholder={acaoTipo === "S" ? "Ex: Aprovado. Boas férias!" : "Ex: Período não disponível. Por favor reagende."}
                  style={{ borderRadius:8, fontSize:13 }}
                />
              </div>

              {/* Botões */}
              <div style={{ display:"flex", gap:10 }}>
                <Button block onClick={() => setModalAcaoOpen(false)} style={{ borderRadius:10, height:42, fontWeight:600 }}>
                  Cancelar
                </Button>
                <Button
                  block loading={submitting} onClick={submitAcao}
                  style={{
                    height:42, borderRadius:10, fontSize:14, fontWeight:700,
                    background: acaoTipo === "S"
                      ? "linear-gradient(135deg,#059669,#10B981)"
                      : "linear-gradient(135deg,#DC2626,#EF4444)",
                    border:"none", color:"#fff",
                    boxShadow: acaoTipo === "S"
                      ? "0 4px 14px rgba(5,150,105,0.35)"
                      : "0 4px 14px rgba(220,38,38,0.35)",
                  }}
                >
                  {submitting
                    ? "A processar..."
                    : acaoTipo === "S" ? "✓ Confirmar Aprovação" : "✕ Confirmar Recusa"
                  }
                </Button>
              </div>
            </div>
          )}
        </Modal>

      </Content>
    </Layout>
  );
};

export default AprovacaoFeriasSecretariado;